import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName, useUsersMap, useUsersQuery } from "@tellescope/tanstack-query";
import { BlockProps } from "../types";
import { useBlock } from ".";

export type CareTeamUser = TellescopeModelFromName<"users">;

export interface UseCareTeamBlockArgs extends BlockProps {}

export const useCareTeamBlock = ({
  enduserId,
  info,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  viewId,
  blockId,
  blockIndex,
  blockLayout,
}: UseCareTeamBlockArgs) => {
  const { t } = useTranslation("contacts");

  const block = useBlock({
    type: "information.care-team",
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout,
    select: (enduser) => enduser.assignedTo ?? [],
  });

  const assignedTo = useMemo(() => block.enduserQuery.data ?? [], [block.enduserQuery.data]);

  // Resolve the assigned user ids into full user records so members render with name + avatar.
  const membersMap = useUsersMap(assignedTo);
  const members = useMemo<CareTeamUser[]>(
    () =>
      assignedTo.reduce<CareTeamUser[]>((acc, id) => {
        const user = membersMap[id];
        if (user) acc.push(user);
        return acc;
      }, []),
    [assignedTo, membersMap]
  );

  const { data: allUsers } = useUsersQuery();

  // Users available to add: exclude anyone already on the care team.
  const addOptions = useMemo<CareTeamUser[]>(
    () => (allUsers ?? []).filter((user) => !assignedTo.includes(user.id)),
    [allUsers, assignedTo]
  );

  const addMember = useCallback(
    (userId: string) => {
      if (assignedTo.includes(userId)) return;
      block.updateEnduserMutation.mutateAsync({
        id: enduserId,
        assignedTo: [...assignedTo, userId],
        _options: { replaceObjectFields: true },
      });
    },
    [assignedTo, enduserId, block.updateEnduserMutation]
  );

  const removeMember = useCallback(
    (userId: string) => {
      block.updateEnduserMutation.mutateAsync({
        id: enduserId,
        assignedTo: assignedTo.filter((id) => id !== userId),
        _options: { replaceObjectFields: true },
      });
    },
    [assignedTo, enduserId, block.updateEnduserMutation]
  );

  return {
    ...block,
    t,
    members,
    addOptions,
    addMember,
    removeMember,
  };
};
